import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DEMOS, MEDIA_BASE } from './demosData';

// Demos page: which video is playing, chapter jumps, and deep links (/demos#evidence).
const log = (...args) => console.log('[DemosPage]', ...args);
const pick = (hash) => {
  const id = (hash || '').replace('#', '');
  return DEMOS.some((d) => d.id === id) ? id : DEMOS[0].id;
};
const videoIn = (root) => (root ? root.querySelector('video') : null);
const playIn = (root) => {
  const v = videoIn(root);
  if (!v) return;
  const p = v.play();
  if (p && p.catch) p.catch((err) => log(`playback did not start (${err.name})`));
};
// phones: the list sits below the player, so bring the player back into view
const showPlayer = () => {
  const p = document.getElementById('player');
  if (!p) return;
  const r = p.getBoundingClientRect();
  if (r.top < 0 || r.top > window.innerHeight * 0.5) p.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function useDemosPage() {
  const rootRef = useRef(null);
  const location = useLocation();
  const [id, setId] = useState(() => pick(location.hash));
  const playNext = useRef(false);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('dm-html');
    window.scrollTo(0, 0);
    log(`page shown with ${DEMOS.length} videos`);
    return () => html.classList.remove('dm-html');
  }, []);

  // links such as /demos#evidence from the home page
  useEffect(() => {
    const next = pick(location.hash);
    log(`deep link: ${location.hash || '(none)'} -> ${next}`);
    setId(next);
  }, [location.hash]);

  // after the source switches because someone picked a video, start playing it
  useEffect(() => {
    if (!playNext.current) return;
    playNext.current = false;
    playIn(rootRef.current);
  }, [id]);

  const select = useCallback((e, next) => {
    if (e) e.preventDefault();
    log(`selected ${next}`);
    window.history.replaceState(window.history.state, '', `#${next}`);
    if (next === id) playIn(rootRef.current);
    else playNext.current = true;
    setId(next);
    window.setTimeout(showPlayer, 60);
  }, [id]);

  const seek = useCallback((e, t) => {
    if (e) e.preventDefault();
    const v = videoIn(rootRef.current);
    if (!v) return;
    log(`chapter: jump to ${t}s`);
    const go = () => { v.currentTime = t; playIn(rootRef.current); showPlayer(); };
    if (v.readyState >= 1) go();
    else v.addEventListener('loadedmetadata', go, { once: true });
  }, []);

  return useMemo(() => {
    const cur = DEMOS.find((d) => d.id === id) || DEMOS[0];
    return {
      rootRef,
      videoSrc: MEDIA_BASE + cur.file,
      poster: cur.poster,
      kicker: cur.kicker,
      dur: cur.dur,
      title: cur.title,
      desc: cur.desc,
      hasChapters: cur.chapters.length > 0,
      chapters: cur.chapters.map((c) => ({
        href: `#t${c.t}`, time: c.time, label: c.label, seek: (e) => seek(e, c.t),
      })),
      count: String(DEMOS.length),
      items: DEMOS.map((d) => ({
        href: `#${d.id}`,
        cls: d.id === cur.id ? 'dm-card dm-on' : 'dm-card',
        current: d.id === cur.id ? 'true' : 'false',
        thumb: d.thumb,
        alt: d.title,
        kicker: d.kicker,
        dur: d.dur,
        title: d.title,
        select: (e) => select(e, d.id),
      })),
    };
  }, [id, select, seek]);
}
